import React from 'react';
import { Check, Clock3, Minus, Mail, Zap } from 'lucide-react';
import { BaseTool, IToolComponentProps, IToolContext, IToolManifest } from '../../core/tools/interfaces/ITool';
import { CompositeCommand, ICommand } from '../../core/commands/ICommand';
import type { Node } from '../../core/models/scene';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { UpdateFlowchartNodeCommand } from '../../domains/process/commands/UpdateFlowchartNodeCommand';
import { useDiagramStore } from '../../stores/diagramStore';
import { useTranslation } from '../../hooks/useTranslation';

type EventType = 'start' | 'intermediate' | 'end' | 'boundary';
type EventMode = 'catching' | 'throwing';
type TriggerType =
  | 'none'
  | 'message'
  | 'timer'
  | 'escalation'
  | 'conditional'
  | 'link'
  | 'error'
  | 'cancel'
  | 'compensation'
  | 'signal'
  | 'multiple'
  | 'parallel-multiple'
  | 'terminate';

type EventPreset = {
  id: string;
  labelKey: string;
  eventType: EventType;
  interrupting: boolean;
  eventMode: EventMode;
};

type BpmnEventNode = Node & {
  eventType?: EventType;
  interrupting?: boolean;
  eventMode?: EventMode;
  trigger?: TriggerType;
};

type BpmnEventPatch = Partial<Node> & {
  eventType?: EventType;
  interrupting?: boolean;
  eventMode?: EventMode;
  trigger?: TriggerType;
};

type SelectedShapeWithData = {
  data?: BpmnEventNode;
};

const EVENT_PRESETS: EventPreset[] = [
  { id: 'start', labelKey: 'tools.bpmnEvent.presets.start', eventType: 'start', interrupting: true, eventMode: 'catching' },
  { id: 'start-non-interrupting', labelKey: 'tools.bpmnEvent.presets.start-non-interrupting', eventType: 'start', interrupting: false, eventMode: 'catching' },
  { id: 'start-throwing', labelKey: 'tools.bpmnEvent.presets.start-throwing', eventType: 'start', interrupting: true, eventMode: 'throwing' },
  { id: 'intermediate', labelKey: 'tools.bpmnEvent.presets.intermediate', eventType: 'intermediate', interrupting: true, eventMode: 'catching' },
  { id: 'intermediate-non-interrupting', labelKey: 'tools.bpmnEvent.presets.intermediate-non-interrupting', eventType: 'intermediate', interrupting: false, eventMode: 'catching' },
  { id: 'intermediate-throwing', labelKey: 'tools.bpmnEvent.presets.intermediate-throwing', eventType: 'intermediate', interrupting: true, eventMode: 'throwing' },
  { id: 'end', labelKey: 'tools.bpmnEvent.presets.end', eventType: 'end', interrupting: true, eventMode: 'throwing' },
];

const CompensationIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="none" className={className}>
    <path d="M11.8 7.2L6.4 12L11.8 16.8Z" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
    <path d="M17.6 7.2L12.2 12L17.6 16.8Z" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
  </svg>
);

const CancelIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="none" className={className}>
    <path
      d="M14.758 7.121L16.879 9.242L14.121 12L16.879 14.758L14.758 16.879L12 14.121L9.242 16.879L7.121 14.758L9.879 12L7.121 9.242L9.242 7.121L12 9.879Z"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinejoin="round"
    />
  </svg>
);

const SignalIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="none" className={className}>
    <path d="M12 6.2L17.8 17H6.2L12 6.2Z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
  </svg>
);

const ConditionalIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="none" className={className}>
    <rect x="6.2" y="5.8" width="11.6" height="12.4" stroke="currentColor" strokeWidth="1.8" />
    <line x1="8.5" y1="9.4" x2="15.5" y2="9.4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    <line x1="8.5" y1="12" x2="15.5" y2="12" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    <line x1="8.5" y1="14.6" x2="15.5" y2="14.6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
  </svg>
);

const EscalationIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="none" className={className}>
    <path d="M12 5.2L17.6 17.2L12 13.2L6.4 17.2Z" stroke="currentColor" strokeWidth="2" strokeLinejoin="miter" fill="none" />
  </svg>
);

const MultipleIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="none" className={className}>
    <path d="M12 5.6L18.1 10L15.8 17.2H8.2L5.9 10L12 5.6Z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
  </svg>
);

const ParallelMultipleIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="none" className={className}>
    <path d="M10.5 6.6H13.5V10.5H17.4V13.5H13.5V17.4H10.5V13.5H6.6V10.5H10.5V6.6Z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
  </svg>
);

const LinkIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="none" className={className}>
    <path
      d="M5.6 10.4H12.7V8L18.4 12L12.7 16V13.6H5.6V10.4Z"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinejoin="round"
      strokeLinecap="round"
    />
  </svg>
);

const TerminateIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="none" className={className}>
    <circle cx="12" cy="12" r="6.5" stroke="currentColor" strokeWidth="2" fill="currentColor" />
  </svg>
);

const TRIGGER_OPTIONS: Array<{ value: TriggerType; labelKey: string; Icon: React.ComponentType<{ className?: string }> }> = [
  { value: 'none', labelKey: 'tools.bpmnEvent.triggers.none', Icon: Minus },
  { value: 'message', labelKey: 'tools.bpmnEvent.triggers.message', Icon: Mail },
  { value: 'timer', labelKey: 'tools.bpmnEvent.triggers.timer', Icon: Clock3 },
  { value: 'escalation', labelKey: 'tools.bpmnEvent.triggers.escalation', Icon: EscalationIcon },
  { value: 'conditional', labelKey: 'tools.bpmnEvent.triggers.conditional', Icon: ConditionalIcon },
  { value: 'link', labelKey: 'tools.bpmnEvent.triggers.link', Icon: LinkIcon },
  { value: 'error', labelKey: 'tools.bpmnEvent.triggers.error', Icon: Zap },
  { value: 'cancel', labelKey: 'tools.bpmnEvent.triggers.cancel', Icon: CancelIcon },
  { value: 'compensation', labelKey: 'tools.bpmnEvent.triggers.compensation', Icon: CompensationIcon },
  { value: 'signal', labelKey: 'tools.bpmnEvent.triggers.signal', Icon: SignalIcon },
  { value: 'multiple', labelKey: 'tools.bpmnEvent.triggers.multiple', Icon: MultipleIcon },
  { value: 'parallel-multiple', labelKey: 'tools.bpmnEvent.triggers.parallel-multiple', Icon: ParallelMultipleIcon },
  { value: 'terminate', labelKey: 'tools.bpmnEvent.triggers.terminate', Icon: TerminateIcon },
];

const normalizeVariant = (node?: { attrs?: Record<string, unknown>; name?: unknown; type?: unknown } | null): string => {
  const attrs = node?.attrs || {};
  const raw = attrs.variant || attrs.shapeType || node?.name || node?.type || '';
  return String(raw).toLowerCase().replace(/\s+/g, '-');
};

const isBpmnEventNode = (node?: Node | null): node is BpmnEventNode => normalizeVariant(node) === 'bpmn-event';

const getSelectedEventNodes = (context: IToolContext): BpmnEventNode[] => {
  if (typeof context.getSelectedShapes !== 'function') return [];
  return context
    .getSelectedShapes()
    .map((shape) => (shape as SelectedShapeWithData).data)
    .filter((node): node is BpmnEventNode => !!node && isBpmnEventNode(node));
};

const detectPresetId = (node: BpmnEventNode): string => {
  const attrs = node.attrs || {};
  const eventType = (attrs.eventType ?? node?.eventType ?? 'start') as EventType;
  const interrupting = Boolean(attrs.interrupting ?? node?.interrupting ?? true);
  const eventMode = (attrs.eventMode ?? node?.eventMode ?? (eventType === 'end' ? 'throwing' : 'catching')) as EventMode;
  const matched = EVENT_PRESETS.find(
    (preset) =>
      preset.eventType === eventType &&
      preset.interrupting === interrupting &&
      preset.eventMode === eventMode
  );
  return matched?.id ?? 'start';
};

const EventPresetIcon: React.FC<{ presetId: string; className?: string }> = ({ presetId, className }) => {
  const preset = EVENT_PRESETS.find((p) => p.id === presetId) ?? EVENT_PRESETS[0];
  const dashed = !preset.interrupting || (preset.eventType === 'intermediate' && preset.eventMode === 'throwing');
  const thick = preset.eventType === 'end' || (preset.eventMode === 'throwing' && preset.eventType !== 'intermediate');
  const outerStrokeWidth = thick ? 2.8 : 1.8;
  return (
    <svg viewBox="0 0 24 24" fill="none" className={className}>
      <circle
        cx="12"
        cy="12"
        r="8"
        stroke={preset.eventType === 'end' ? '#0F172A' : 'currentColor'}
        strokeWidth={outerStrokeWidth}
        strokeDasharray={dashed ? '2.5 2.5' : undefined}
        fill={preset.eventType === 'end' ? '#0F172A' : 'none'}
      />
      {preset.eventType === 'intermediate' && (
        <circle
          cx="12"
          cy="12"
          r="5.3"
          stroke="currentColor"
          strokeWidth={thick ? 2.2 : 1.6}
          strokeDasharray={dashed ? '2.5 2.5' : undefined}
          fill="none"
        />
      )}
    </svg>
  );
};

const BpmnEventStyleToolComponent: React.FC<IToolComponentProps> = ({ context, onExecute }) => {
  const { t } = useTranslation('diagram');
  const [open, setOpen] = React.useState(false);
  const selectedEvents = React.useMemo(() => getSelectedEventNodes(context), [context, context.selection]);
  const currentPresetId = React.useMemo(() => {
    if (selectedEvents.length === 0) return 'start';
    const first = detectPresetId(selectedEvents[0]);
    const allSame = selectedEvents.every((node) => detectPresetId(node) === first);
    return allSame ? first : first;
  }, [selectedEvents]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid="tid.tools.bpmnEvent.style.trigger"
          className="w-8 h-8 flex items-center justify-center hover:bg-muted rounded transition-colors"
          aria-label={t('tools.bpmnEvent.style', { defaultValue: 'Event style' })}
        >
          <EventPresetIcon presetId={currentPresetId} className="h-5 w-5" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[320px] p-2" align="start">
        <div className="space-y-1">
          {EVENT_PRESETS.map((preset) => {
            const isActive = preset.id === currentPresetId;
            return (
              <button
                key={preset.id}
                type="button"
                data-testid={`tid.tools.bpmnEvent.style.${preset.id}`}
                className="w-full flex items-center gap-3 px-3 py-2 rounded hover:bg-muted transition-colors text-left"
                onClick={() => {
                  onExecute({
                    eventType: preset.eventType,
                    interrupting: preset.interrupting,
                    eventMode: preset.eventMode,
                  });
                  setOpen(false);
                }}
              >
                <EventPresetIcon presetId={preset.id} className="h-5 w-5 text-foreground" />
                <span className="text-[14px] text-foreground">{t(preset.labelKey)}</span>
                {isActive && <Check className="ml-auto h-4 w-4 text-foreground" />}
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
};

const BpmnEventTriggerToolComponent: React.FC<IToolComponentProps> = ({ context, onExecute }) => {
  const { t } = useTranslation('diagram');
  const [open, setOpen] = React.useState(false);
  const selectedEvents = React.useMemo(() => getSelectedEventNodes(context), [context, context.selection]);
  const nodeTrigger = React.useCallback(
    (node: BpmnEventNode) => String(node.attrs?.trigger ?? node.trigger ?? 'none').toLowerCase() as TriggerType,
    [],
  );
  const currentTrigger = React.useMemo(() => {
    if (selectedEvents.length === 0) return 'none' as TriggerType;
    const first = nodeTrigger(selectedEvents[0]);
    const allSame = selectedEvents.every((node) => nodeTrigger(node) === first);
    return allSame ? first : first;
  }, [nodeTrigger, selectedEvents]);
  const TriggerIcon = (TRIGGER_OPTIONS.find((o) => o.value === currentTrigger)?.Icon ?? Minus);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid="tid.tools.bpmnEvent.trigger.trigger"
          className="w-8 h-8 flex items-center justify-center hover:bg-muted rounded transition-colors"
          aria-label={t('tools.bpmnEvent.trigger', { defaultValue: 'Event trigger' })}
        >
          <TriggerIcon className="h-5 w-5 text-foreground" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[300px] p-2" align="start">
        <div className="space-y-1 max-h-[360px] overflow-y-auto pr-1">
          {TRIGGER_OPTIONS.map((option) => {
            const Icon = option.Icon;
            const isActive = option.value === currentTrigger;
            return (
              <button
                key={option.value}
                type="button"
                data-testid={`tid.tools.bpmnEvent.trigger.${option.value}`}
                className="w-full flex items-center gap-3 px-3 py-2 rounded hover:bg-muted transition-colors text-left"
                onClick={() => {
                  onExecute({ trigger: option.value });
                  setOpen(false);
                }}
              >
                <Icon className="h-5 w-5 text-foreground" />
                <span className="text-[14px] text-foreground">{t(option.labelKey)}</span>
                {isActive && <Check className="ml-auto h-4 w-4 text-foreground" />}
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
};

const buildPatchCommand = (nodeId: string, node: BpmnEventNode, patch: BpmnEventPatch) => {
  const changes: BpmnEventPatch = {
    ...patch,
    attrs: { ...(node.attrs || {}), ...patch },
  };
  return new UpdateFlowchartNodeCommand({
    nodeId,
    changes,
  });
};

export class BpmnEventStyleTool extends BaseTool {
  manifest: IToolManifest = {
    id: 'bpmn-event-style',
    name: 'tools.bpmnEvent.style',
    icon: 'circle',
    category: 'style',
    description: 'Change BPMN event ring style',
    version: '1.0.0',
    applicability: { diagramTypes: ['flowchart'], contextTypes: ['genericShape'] },
    requiresSelection: true,
    minSelection: 1,
    ui: { toolbar: true, contextMenu: false, propertyPanel: false, sidebar: false, floatingPanel: true, statusBar: false },
    capabilities: {
      modifiesShapes: true,
      createsShapes: false,
      deletesShapes: false,
      modifiesConnectors: false,
      createsConnectors: false,
      deletesConnectors: false,
      requiresUserInput: true,
      supportsMultiSelect: true,
      supportsBatchOperations: true,
    },
  };

  canExecute(context: IToolContext): boolean {
    if (!super.canExecute(context) || context.diagramType !== 'flowchart' || context.selection.size === 0) return false;
    const ids = Array.from(context.selection);
    const nodes = useDiagramStore.getState().nodes.filter((n) => ids.includes(n.id));
    return nodes.length > 0 && nodes.every((n) => isBpmnEventNode(n));
  }

  execute(params?: { eventType?: EventType; interrupting?: boolean; eventMode?: EventMode }): ICommand | null {
    if (!this.context || this.context.selection.size === 0 || !params) return null;
    const ids = Array.from(this.context.selection);
    const byId = new Map(useDiagramStore.getState().nodes.map((n) => [n.id, n]));
    const commands = ids
      .map((id) => {
        const node = byId.get(id);
        if (!node || !isBpmnEventNode(node)) return null;
        return buildPatchCommand(id, node, {
          eventType: params.eventType,
          interrupting: params.interrupting,
          eventMode: params.eventMode,
        });
      })
      .filter(Boolean) as ICommand[];
    if (commands.length === 0) return null;
    if (commands.length === 1) return commands[0];
    return new CompositeCommand('Update BPMN Event Style', commands);
  }

  getToolbarComponent() { return BpmnEventStyleToolComponent; }
  getFloatingPanelComponent() { return BpmnEventStyleToolComponent; }
}

export class BpmnEventTriggerTool extends BaseTool {
  manifest: IToolManifest = {
    id: 'bpmn-event-trigger',
    name: 'tools.bpmnEvent.trigger',
    icon: 'clock',
    category: 'style',
    description: 'Change BPMN event trigger icon',
    version: '1.0.0',
    applicability: { diagramTypes: ['flowchart'], contextTypes: ['genericShape'] },
    requiresSelection: true,
    minSelection: 1,
    ui: { toolbar: true, contextMenu: false, propertyPanel: false, sidebar: false, floatingPanel: true, statusBar: false },
    capabilities: {
      modifiesShapes: true,
      createsShapes: false,
      deletesShapes: false,
      modifiesConnectors: false,
      createsConnectors: false,
      deletesConnectors: false,
      requiresUserInput: true,
      supportsMultiSelect: true,
      supportsBatchOperations: true,
    },
  };

  canExecute(context: IToolContext): boolean {
    if (!super.canExecute(context) || context.diagramType !== 'flowchart' || context.selection.size === 0) return false;
    const ids = Array.from(context.selection);
    const nodes = useDiagramStore.getState().nodes.filter((n) => ids.includes(n.id));
    return nodes.length > 0 && nodes.every((n) => isBpmnEventNode(n));
  }

  execute(params?: { trigger?: TriggerType }): ICommand | null {
    if (!this.context || this.context.selection.size === 0 || !params?.trigger) return null;
    const ids = Array.from(this.context.selection);
    const byId = new Map(useDiagramStore.getState().nodes.map((n) => [n.id, n]));
    const commands = ids
      .map((id) => {
        const node = byId.get(id);
        if (!node || !isBpmnEventNode(node)) return null;
        return buildPatchCommand(id, node, { trigger: params.trigger });
      })
      .filter(Boolean) as ICommand[];
    if (commands.length === 0) return null;
    if (commands.length === 1) return commands[0];
    return new CompositeCommand('Update BPMN Event Trigger', commands);
  }

  getToolbarComponent() { return BpmnEventTriggerToolComponent; }
  getFloatingPanelComponent() { return BpmnEventTriggerToolComponent; }
}
